import { PostCheckoutMandatoryQuestionsInAppVariant } from '../../../constants/statsig';
import { CheckoutState } from '../../../state';
import { getPostCheckoutMandatoryQuestionsInAndroidApp } from '../../../utils/statsig';
import {
    getIsOrganizationExcludedFromPostCheckoutMandatoryQuestions,
    isAndroidApp,
    isMyOrderFlow,
} from '../../app';
import { getIsNosSourceOfTruth, isSalesClient } from '../../event';
import { hasOfflinePaymentMethods } from '../../order/typedOrderSelectors';
import { getIsAskRequiredNonWaiverQuestionsAfterPaymentEnabled } from '../../splitio';
import { getHasInventoryQuestions } from './getHasInventoryQuestions';
import { getHasTaxQuestions } from './getHasTaxQuestions';
import { getHasRequiredWaiverQuestions } from './getHasWaiverQuestions';

export enum SurveyDistribution {
    onlyOptionalQuestionsAfterPayment = 'onlyOptionalQuestionsAfterPayment',
    cannedQuestionsAndOptionalQuestionsAfterPayment = 'cannedQuestionsAndOptionalQuestionsAfterPayment',
    allQuestionsAfterPayment = 'allQuestionsAfterPayment',
    allButRequiredInventoryAndRequiredWaiverQuestionsAfterPayment = 'allButRequiredInventoryAndRequiredWaiverQuestionsAfterPayment',
}

const surveyPageByQuestionRequirementStatusByPostCheckoutMandatoryQuestionsInAppVariant: Record<
    PostCheckoutMandatoryQuestionsInAppVariant,
    SurveyDistribution
> = {
    [PostCheckoutMandatoryQuestionsInAppVariant.optionalQuestionsInApp]:
        SurveyDistribution.onlyOptionalQuestionsAfterPayment,
    [PostCheckoutMandatoryQuestionsInAppVariant.optionalAndCannedQuestionsInApp]:
        SurveyDistribution.cannedQuestionsAndOptionalQuestionsAfterPayment,
    [PostCheckoutMandatoryQuestionsInAppVariant.customQuestionsInApp]:
        SurveyDistribution.allQuestionsAfterPayment,
};

/**
 * @private Do not use this directly, derive information from `getSurvey` instead
 */
export const getSurveyDistribution = (
    state: CheckoutState,
): SurveyDistribution => {
    if (isMyOrderFlow(state)) {
        return SurveyDistribution.allQuestionsAfterPayment;
    }

    const isDeferringRequiredQuestionsSupported =
        getIsNosSourceOfTruth(state) &&
        !hasOfflinePaymentMethods(state) &&
        !getHasTaxQuestions(state) &&
        (!getHasRequiredWaiverQuestions(state) ||
            getIsAskRequiredNonWaiverQuestionsAfterPaymentEnabled(state)) &&
        !getHasInventoryQuestions(state);

    const isOrganizerExplicitlyExcluded =
        isSalesClient(state) ||
        getIsOrganizationExcludedFromPostCheckoutMandatoryQuestions(state);

    if (
        !isDeferringRequiredQuestionsSupported ||
        isOrganizerExplicitlyExcluded
    ) {
        return SurveyDistribution.onlyOptionalQuestionsAfterPayment;
    }

    if (isAndroidApp(state)) {
        const postCheckoutMandatoryQuestionsInAndroidAppVariant =
            getPostCheckoutMandatoryQuestionsInAndroidApp();

        return surveyPageByQuestionRequirementStatusByPostCheckoutMandatoryQuestionsInAppVariant[
            postCheckoutMandatoryQuestionsInAndroidAppVariant
        ];
    }

    if (getIsAskRequiredNonWaiverQuestionsAfterPaymentEnabled(state)) {
        return SurveyDistribution.allButRequiredInventoryAndRequiredWaiverQuestionsAfterPayment;
    }

    return SurveyDistribution.allQuestionsAfterPayment;
};
